'use client';

import { z } from 'zod';
import { FieldErrors, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormEvent, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Input } from '@/components/ui/input/input.component';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.component';
import { useLoading } from '@/hooks/use-loading.hook';
import { cpfValidator } from '@/utils/validators';
import { setPendingSignup } from '@/store/pending-signup.store';
import { setAuthTokens } from '@/storage/auth-tokens.storage';
import { StepKeys } from '@/app/(main)/_components/sign-in-modal/_types/header-auth-modal-step-keys.enum';
import { isPendingEmailValidation, login } from '@/services/auth/login.service';
import { resolveAuthenticatedUser } from '@/store/auth.store';
import { modalsOpeningStore, setOpenSignInModal } from '@/store/modal';
import { useFocus } from '@/hooks/use-focus.hook';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { useToast } from '@/components/ui/use-toast';
import { HttpException } from '@/utils/api/api-client';
import { visualEmailSac, visualPhonePatientSac } from '@/utils/consts/sac.consts';
import {
  LoginAnalyticsEnum,
  LoginErrorType,
  VerificationCodeFlow,
} from '@/types/enums/analytics/login/login-analytics.enum';
import { trackLoginModalEvent } from '@/utils/auth/login-analytics.util';

export const LOGIN_REFUSAL_MESSAGE = {
  customerBlocked: 'Sua conta não pode fazer novas compras no momento. '
    + `Para entender o motivo, fale com a gente pelo WhatsApp ${visualPhonePatientSac}.`,
  deletionRequested: 'Você solicitou a exclusão dos seus dados e não poderá fazer login. '
    + `Em caso de dúvidas, entre em contato através do ${visualEmailSac}.`,
};

const LOGIN_REFUSAL_BY_API_MESSAGE: Record<string, string> = {
  'User is blocked': LOGIN_REFUSAL_MESSAGE.customerBlocked,
  'User is not active': LOGIN_REFUSAL_MESSAGE.deletionRequested,
};

function loginRefusalMessage(error: unknown): string | undefined {
  if (!(error instanceof HttpException)) {
    return undefined;
  }

  const apiMessage = (error.data as { message?: string } | undefined)?.message;

  return apiMessage ? LOGIN_REFUSAL_BY_API_MESSAGE[apiMessage] : undefined;
}

const loginFormSchema = z.object({
  documentNumber: z.custom<string>(cpfValidator, {
    message: 'CPF inválido.',
  }),
  password: z.string().min(1, {
    message: 'Informe sua senha.',
  }),
});

type LoginForm = z.infer<typeof loginFormSchema>;

const defaultValues: Partial<LoginForm> = {
  documentNumber: '',
  password: '',
};

export default function LoginForm() {
  const loginCallback = useStore(modalsOpeningStore, (state) => state.loginCallback);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { handleJump } = useProgressiveModal<StepKeys>();
  const { startLoading, stopLoading, isLoading } = useLoading();
  const { htmlElRef, setFocus } = useFocus();
  const { toast } = useToast();

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginFormSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  const handleForgotPassword = () => {
    trackLoginModalEvent(LoginAnalyticsEnum.LOGIN_CLICK_FORGOT_PASSWORD);
    handleJump({ key: StepKeys.FORGOT_PASSWORD_CPF });
  };

  const onValid = async ({ documentNumber, password }: LoginForm) => {
    try {
      startLoading();

      const isPendingEmailValidationResponse = await isPendingEmailValidation(documentNumber, password);
      if (
        isPendingEmailValidationResponse
        && isPendingEmailValidationResponse.isPendingValidation
        && !isPendingEmailValidationResponse.isProfessional
      ) {
        const query = searchParams?.toString();

        setPendingSignup({
          token: isPendingEmailValidationResponse.token,
          maskedEmail: isPendingEmailValidationResponse.maskedEmail,
          returnUrl: query ? `${pathname}?${query}` : pathname,
          origin: modalsOpeningStore.getState().signInModalOrigin,
          flow: VerificationCodeFlow.EMAIL_PENDENTE,
        });

        trackLoginModalEvent(LoginAnalyticsEnum.PENDING_EMAIL_CODE_REQUESTED);
        handleJump({ key: StepKeys.PIN_CONFIRMATION });
        return;
      }
      const loginResponse = await login(documentNumber, password);

      if (!loginResponse.access_token) {
        trackLoginModalEvent(LoginAnalyticsEnum.LOGIN_ERROR, {
          error_type: LoginErrorType.CREDENCIAIS_INVALIDAS,
        });
        toast.error('CPF ou senha incorretos. Tente novamente.');
        return;
      }

      const { access_token, refresh_token } = loginResponse;
      setAuthTokens({ access_token, refresh_token });

      trackLoginModalEvent(LoginAnalyticsEnum.LOGIN_SUCCESS);
      setOpenSignInModal(false);
      await resolveAuthenticatedUser(access_token);
      loginCallback?.();
    } catch (error) {
      const errorType = error instanceof HttpException && error.status === 401
        ? LoginErrorType.CREDENCIAIS_INVALIDAS
        : LoginErrorType.SERVIDOR;

      trackLoginModalEvent(LoginAnalyticsEnum.LOGIN_ERROR, { error_type: errorType });
      const refusalMessage = loginRefusalMessage(error);

      if (refusalMessage) {
        toast.error(refusalMessage);
        return;
      }

      const accessLimitExceeded = error instanceof HttpException && error.status === 429;
      toast.error(
        accessLimitExceeded
          ? 'Limite de tentativas de acesso excedido. Tente novamente mais tarde.'
          : 'CPF ou senha incorretos. Tente novamente.',
      );
    } finally {
      stopLoading();
    }
  };

  const onInvalid = (errors: FieldErrors<LoginForm>) => {
    if (errors.documentNumber) {
      trackLoginModalEvent(LoginAnalyticsEnum.LOGIN_INVALID_CPF);
    }
    if (errors.password) {
      trackLoginModalEvent(LoginAnalyticsEnum.LOGIN_INVALID_PASSWORD);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    trackLoginModalEvent(LoginAnalyticsEnum.LOGIN_CLICK_SUBMIT);
    return form.handleSubmit(onValid, onInvalid)(event);
  };

  useEffect(() => {
    setFocus();
  }, []);

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} aria-label="form">
        <FormField
          control={form.control}
          name="documentNumber"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>CPF</FormLabel>
              <FormControl>
                <Input
                  data-testid={DataTestIdEnum.LOGIN_INPUT_CPF}
                  placeholder="000.000.000-00"
                  mask="999.999.999-99"
                  {...field}
                  inputMode="numeric"
                  ref={htmlElRef}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="mt-6">
              <FormLabel>Senha</FormLabel>
              <FormControl>
                <Input
                  data-testid={DataTestIdEnum.LOGIN_INPUT_PASSWORD}
                  autoFocus
                  placeholder="Digite a sua senha"
                  type="password"
                  maxLength={20}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="button"
          color="primary"
          variant="text"
          className="mt-4 justify-start"
          onClick={handleForgotPassword}
        >
          Esqueci minha senha
        </Button>

        <Button
          data-testid={DataTestIdEnum.LOGIN_SUBMIT_BUTTON}
          isLoading={isLoading}
          type="submit"
          color="primary"
          variant="default"
          className="mt-6 w-full h-12"
        >
          Entrar
        </Button>
      </form>
    </Form>
  );
}
